import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Hls from "hls.js";
import { Volume2, VolumeX, Play, EyeOff, X, Maximize2 } from "lucide-react";
import { api } from "@/lib/api";

const PREVIEW_MAX_H = 250;

function attachHls(v, hls) {
  if (v.canPlayType("application/vnd.apple.mpegurl")) { v.src = hls; return null; }
  if (Hls.isSupported()) { const h = new Hls({ maxBufferLength: 10 }); h.loadSource(hls); h.attachMedia(v); return h; }
  return null;
}

// Visualizador ampliado: proporção original, sem cortes, controlos nativos
function VideoLightbox({ src, ratio, startAt, onClose, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const h = attachHls(v, src.hls);
    const start = () => { if (startAt) v.currentTime = startAt; v.play().catch(() => {}); };
    v.onloadedmetadata = start;
    if (h) h.on(Hls.Events.MANIFEST_PARSED, start);
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { h?.destroy(); v.onloadedmetadata = null; document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [src, startAt, onClose]);
  return createPortal(
    <div data-testid={`${testid}-viewer`} role="dialog" aria-modal="true" aria-label="Vídeo" onClick={onClose}
         className="fixed inset-0 z-[100] bg-black flex items-center justify-center p-3 sm:p-6">
      <button type="button" onClick={onClose} aria-label="Fechar" data-testid={`${testid}-viewer-close`}
              className="absolute top-3 right-3 z-10 bg-white/15 hover:bg-white/30 text-white rounded-full p-2.5" style={{ marginTop: "env(safe-area-inset-top)" }}>
        <X size={20} />
      </button>
      <video ref={ref} data-testid={`${testid}-viewer-el`} controls playsInline autoPlay poster={src.thumbnail} onClick={(e) => e.stopPropagation()}
             style={{ aspectRatio: `${ratio}`, maxWidth: "100%", maxHeight: "calc(100dvh - 1.5rem)", width: `min(100%, calc((100dvh - 1.5rem) * ${ratio.toFixed(4)}))` }}
             className="bg-black rounded-lg object-contain" />
    </div>,
    document.body,
  );
}

export function VideoPlayer({ video, testid = "video-player" }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retried = useRef(false);
  const [src, setSrc] = useState(null);
  const [muted, setMuted] = useState(true);
  const [state, setState] = useState("idle"); // idle | loading | ready | blocked | error
  const [visible, setVisible] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [open, setOpen] = useState(false);
  const [startAt, setStartAt] = useState(0);
  const [dims, setDims] = useState(() => (video?.width && video?.height ? { w: video.width, h: video.height } : null));

  const ratio = dims ? dims.w / dims.h : 16 / 9;
  const orientation = !dims ? "landscape" : Math.abs(ratio - 1) < 0.05 ? "square" : ratio < 1 ? "portrait" : "landscape";
  // Pré-visualização compacta: largura total, altura ≤ 250px, recorte centralizado (só na pré-visualização; o ficheiro não muda)
  const frame = { aspectRatio: `${ratio}`, maxHeight: PREVIEW_MAX_H, width: "100%" };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver((es) => setVisible(es.some((e) => e.isIntersecting && e.intersectionRatio >= 0.6)), { threshold: [0, 0.6] });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || src || state !== "idle" || !video?.id) return;
    setState("loading");
    api.post(`/videos/${video.id}/playback`).then((r) => { setSrc(r.data); setState("ready"); if (r.data.width && r.data.height) setDims({ w: r.data.width, h: r.data.height }); })
       .catch((e) => setState(e?.response?.status === 403 ? "blocked" : "error"));
  }, [visible, src, state, video?.id]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    v.defaultMuted = true; v.muted = true; // Safari/iOS: atributo muted necessário para autoplay
    const expire = () => {
      const expired = src.expires_at && Date.now() / 1000 >= src.expires_at - 5;
      if (expired || !retried.current) { retried.current = !expired; hlsRef.current?.destroy(); hlsRef.current = null; setSrc(null); setState("idle"); } // token expirado → pede outro (autorização revalidada no servidor)
      else setState("error");
    };
    v.onerror = expire;
    const h = attachHls(v, src.hls);
    if (h) { hlsRef.current = h; h.on(Hls.Events.ERROR, (_, d) => d.fatal && expire()); }
    return () => { hlsRef.current?.destroy(); hlsRef.current = null; v.onerror = null; };
  }, [src]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    if (visible && !open) v.play().then(() => setNeedsTap(false)).catch(() => setNeedsTap(true)); else v.pause();
  }, [visible, src, open]);

  const openViewer = () => { if (!src) return; setStartAt(videoRef.current?.currentTime || 0); setOpen(true); };
  const closeViewer = () => setOpen(false);

  if (state === "blocked") return <div data-testid={`${testid}-blocked`} className="mt-2 rounded-xl bg-zinc-100 text-zinc-500 text-xs p-4 flex items-center gap-2"><EyeOff size={14} /> Vídeo indisponível para a tua conta.</div>;
  if (state === "error") return <div data-testid={`${testid}-error`} className="mt-2 rounded-xl bg-zinc-100 text-zinc-500 text-xs p-4">Vídeo indisponível.</div>;

  return (
    <>
      <div ref={wrapRef} data-testid={testid} data-orientation={orientation} style={frame} className="relative mt-2 rounded-xl overflow-hidden bg-black cursor-zoom-in" onClick={openViewer}>
        <video ref={videoRef} data-testid={`${testid}-el`} playsInline muted={muted} loop preload="metadata" poster={src?.thumbnail}
               onLoadedMetadata={(e) => { const { videoWidth: w, videoHeight: h } = e.currentTarget; if (w && h && (!dims || dims.w !== w || dims.h !== h)) setDims({ w, h }); }}
               className="absolute inset-0 w-full h-full object-cover object-center" />
        {(!src || needsTap) && <div data-testid={`${testid}-tap`} className="absolute inset-0 flex items-center justify-center text-white/80 pointer-events-none"><span className="bg-black/50 rounded-full p-3"><Play size={28} /></span></div>}
        <button type="button" onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }} aria-label={muted ? "Ativar som" : "Silenciar"} data-testid={`${testid}-mute`}
                className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-2">
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        <span aria-hidden="true" className="absolute bottom-2 left-2 bg-black/60 text-white rounded-full p-2"><Maximize2 size={14} /></span>
        {video?.duration && <span className="absolute top-2 left-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">{Math.round(video.duration)}s</span>}
      </div>
      {open && src && <VideoLightbox src={src} ratio={ratio} startAt={startAt} onClose={closeViewer} testid={testid} />}
    </>
  );
}
